import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    Tabs,
    Tab,
    TextField,
    Tooltip,
    Autocomplete,
} from '@mui/material';
import { getCountries, useGeo } from '../../utils/geo';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CloseIcon from "@material-ui/icons/Close";
import { pdf } from '@react-pdf/renderer';
import useStyles from '../../assets/styles/HomeStyles';
import LoaderWithBackDrop from "../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import InvoicePdfDoc from './components/InvoicePdfDoc.comp';
import InvoiceHistoryTab from './components/InvoiceHistoryTab.comp';
import PaymentMethodsTab from './components/PaymentMethodsTab.comp';
import TopUpCreditsDialog from './components/TopUpCreditsDialog.comp';
import InvoiceDetailsDialog from './components/InvoiceDetailsDialog.comp';
import CancelSubscriptionDialog from './components/CancelSubscriptionDialog.comp';
import loadStripeJs from '../../utils/loadStripe';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PaymantInfoService, getSubscriptionStatusService, cancelSubscriptionService, resumeSubscriptionService, getCreditBalanceService, listSavedCardsService, setupCardService, setPrimaryCardService, removeCardService, payPendingInvoiceService } from '../../services/billing.service';
import StripePaymentForm from './components/StripePaymentForm.comp';
import { getStripeConfigService } from '../../services_archived/app.service';
import {
    getInsiderProgramsAndAssessmentsService,
} from '../../services/learning_programs.service';
import { getDQInsiderInfoService, updateProfileDetailsService } from '../../services/auth.service';
import {DQ_PRO_PLAN_KEY, TOAST_TYPES} from '../../utils/constants';
import PlansDialog from './DQProV2/components/PlansDialog.jsx';
import useUpgradeStyles from '../../assets/styles/DQProV2ProgCreatorPreviewStyles';
import { toast } from '../../utils/utils';
import {
    validateBillingDetailsFields,
    sanitizeBillingFieldValue,
    getBillingFieldError,
    getBillingFieldErrors,
} from '../../utils/billingDetailsValidation';


// ── AddCardForm must be defined OUTSIDE DQProInvoiceList ──────────────────────
// Defining it inside the parent component causes React to treat it as a brand-new
// component type on every parent re-render, unmounting and remounting the Stripe
// CardElement iframe. This breaks Stripe's internal message channel and can trigger
// "message channel closed before a response was received" errors.
const AddCardForm = ({ onSuccess, onClose, cardSetupSecret, pendingPaymentInvoice }) => {
    const stripe = useStripe();
    const elements = useElements();
    // Stripe CardElement runs in a sandboxed iframe — CSS variables are not resolved there.
    const isDark = document.documentElement.getAttribute('data-color-mode') === 'dark';
    const stripeTextColor = isDark ? '#E5E7EB' : '#1e293b';
    const stripePlaceholderColor = isDark ? '#6B7280' : '#94a3b8';
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [cardholderName, setCardholderName] = useState('');
    // Synchronous guard — same reason as PlansDialog: React state is async so a
    // double-click can fire confirmCardSetup twice before `processing` state prevents it.
    const submittingRef = React.useRef(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements || !cardSetupSecret) return;
        if (submittingRef.current || processing) return;
        submittingRef.current = true;
        setProcessing(true);
        setError(null);

        try {
            const cardElement = elements.getElement(CardElement);
            const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(cardSetupSecret, {
                payment_method: {
                    card: cardElement,
                    billing_details: { name: cardholderName || 'Card Holder' },
                },
            });

            if (stripeError) {
                setError(stripeError.message);
            } else if (setupIntent.status === 'succeeded') {
                await onSuccess(setupIntent.payment_method);
                onClose();
            }
        } finally {
            submittingRef.current = false;
            setProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            {/* Cardholder Name */}
            <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 600, mb: 0.75, color: 'var(--brand-text-muted, #475569)', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                    Cardholder Name
                </Typography>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: '11px 14px',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '10px',
                    backgroundColor: 'var(--brand-surface, #f8fafc)',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    '&:focus-within': { borderColor: '#ff6468', boxShadow: '0 0 0 3px rgba(255,100,104,0.08)', backgroundColor: 'var(--brand-surface, #fff)' },
                }}>
                    <input
                        value={cardholderName}
                        onChange={(e) => setCardholderName(e.target.value)}
                        placeholder="Full name on card"
                        style={{ border: 'none', outline: 'none', width: '100%', fontSize: '14px', color: 'var(--brand-text, #1e293b)', fontFamily: 'inherit', backgroundColor: 'transparent' }}
                    />
                </Box>
            </Box>

            {/* Card Details */}
            <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontWeight: 600, mb: 0.75, color: 'var(--brand-text-muted, #475569)', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                    Card Number
                </Typography>
                <Box sx={{
                    p: '13px 14px',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '10px',
                    backgroundColor: 'var(--brand-surface, #f8fafc)',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    '&:focus-within': { borderColor: '#ff6468', boxShadow: '0 0 0 3px rgba(255,100,104,0.08)', backgroundColor: 'var(--brand-surface, #fff)' },
                }}>
                    <CardElement options={{
                        style: {
                            base: {
                                fontSize: '14px',
                                color: stripeTextColor,
                                fontFamily: 'inherit',
                                '::placeholder': { color: stripePlaceholderColor },
                                iconColor: '#ff6468',
                            }
                        }
                    }} />
                </Box>
            </Box>

            {/* Error */}
            {error && (
                <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: '#dc2626', fontSize: '0.8rem' }}>{error}</Typography>
                </Box>
            )}

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
                <Button
                    type="button"
                    variant="outlined"
                    onClick={onClose}
                    disabled={processing}
                    fullWidth
                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', borderColor: '#e2e8f0', color: '#64748b', py: 1.25, '&:hover': { borderColor: '#cbd5e1', backgroundColor: 'var(--brand-surface, #f8fafc)' } }}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={!stripe || processing}
                    fullWidth
                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, fontSize: '0.875rem', py: 1.25, backgroundColor: '#ff6468', boxShadow: '0 4px 12px rgba(255,100,104,0.3)', '&:hover': { backgroundColor: '#e05558', boxShadow: '0 6px 16px rgba(255,100,104,0.4)' }, '&.Mui-disabled': { backgroundColor: '#e2e8f0', boxShadow: 'none' } }}
                >
                    {processing
                        ? (pendingPaymentInvoice ? 'Processing...' : 'Saving...')
                        : (pendingPaymentInvoice ? 'Pay Now' : 'Save Card')}
                </Button>
            </Box>
        </form>
    );
};

// Must live outside BillingDetailsForm — inner Field remounts inputs every keystroke (one letter only).
const billingFieldLabelSx = { fontSize: '0.85rem', fontWeight: 600, color: 'var(--brand-text, #111827)', mb: 0.75 };
const billingFieldInputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        backgroundColor: 'var(--brand-surface, #F9FAFB)',
        fontSize: '0.9rem',
        '& fieldset': { borderColor: 'var(--brand-border, #E5E7EB)' },
        '&:hover fieldset': { borderColor: '#D1D5DB' },
        '&.Mui-focused fieldset': { borderColor: '#9CA3AF' },
    },
    '& .MuiOutlinedInput-input': { py: '10px', px: '14px' },
};

const billingCountryInputSx = {
    ...billingFieldInputSx,
    '& .MuiOutlinedInput-root': {
        ...billingFieldInputSx['& .MuiOutlinedInput-root'],
        minHeight: '42px',
        padding: '0 39px 0 0',
    },
    '& .MuiOutlinedInput-input, & .MuiAutocomplete-input': {
        py: '13px',
        px: '14px !important',
    },
    '& .MuiAutocomplete-endAdornment': {
        top: '50%',
        transform: 'translateY(-50%)',
        right: '9px',
    },
};

const BillingDetailField = ({
    label, placeholder, value, onChange, onBlur, required, type, error, helperText,
}) => (
    <Box sx={{ mb: 2.5 }}>
        <Typography sx={billingFieldLabelSx}>
            {label}{required && ' *'}
        </Typography>
        <TextField
            fullWidth
            type={type || 'text'}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            variant="outlined"
            error={Boolean(error)}
            helperText={helperText || ' '}
            sx={billingFieldInputSx}
        />
    </Box>
);

const BillingDetailCountryField = ({ value, onChange, onBlur, error, helperText }) => (
    <Box sx={{ mb: 2.5 }}>
        <Typography sx={billingFieldLabelSx}>Country *</Typography>
        <Autocomplete
            options={getCountries()}
            getOptionLabel={(option) => option.label || ''}
            isOptionEqualToValue={(option, val) => option.code === val?.code}
            value={getCountries().find((c) => c.code === value) || null}
            onChange={onChange}
            onBlur={onBlur}
            fullWidth
            ListboxProps={{
                sx: {
                    maxHeight: 320,
                    '& .MuiAutocomplete-option': { minHeight: 42, py: 1.25 },
                },
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder="Select country"
                    variant="outlined"
                    error={Boolean(error)}
                    helperText={helperText || ' '}
                    sx={billingCountryInputSx}
                />
            )}
        />
    </Box>
);

const applyBillingInitialData = (initialData) => {
    const resolveCountryCode = (raw) => {
        const c = String(raw || '').trim();
        if (!c) return '';
        if (getCountries().some((x) => x.code === c)) return c;
        const byLabel = getCountries().find((x) => x.label === c);
        return byLabel?.code || c.toUpperCase().slice(0, 2);
    };

    return {
        name: initialData.name || '',
        email: initialData.email || '',
        country: resolveCountryCode(initialData.country),
        address: initialData.address || '',
        company: initialData.company || '',
        vat: initialData.vat || '',
        street: initialData.street || '',
        city: initialData.city || '',
        state: initialData.state || '',
        zip: initialData.zip || '',
    };
};

const BillingDetailsForm = ({ initialData = {}, onSave, resetKey = 0 }) => {
    const [fields, setFields] = useState({
        name: '',
        email: '',
        country: '',
        address: '',
        company: '',
        vat: '',
        street: '',
        city: '',
        state: '',
        zip: '',
    });
    const [saving, setSaving] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const hydrated = React.useRef(false);
    const userEdited = React.useRef(false);

    React.useEffect(() => {
        if (hydrated.current || userEdited.current) return;
        if (Object.keys(initialData).length === 0) return;

        hydrated.current = true;
        setFields(applyBillingInitialData(initialData));
    }, [initialData]);

    React.useEffect(() => {
        if (!resetKey || Object.keys(initialData).length === 0) return;
        userEdited.current = false;
        setFields(applyBillingInitialData(initialData));
        setFieldErrors({});
    }, [resetKey, initialData]);

    const handleChange = (key) => (e) => {
        const value = sanitizeBillingFieldValue(key, e.target.value);
        userEdited.current = true;
        setFields((f) => {
            const next = { ...f, [key]: value };
            setFieldErrors((errs) => ({
                ...errs,
                [key]: getBillingFieldError(key, next) || undefined,
            }));
            return next;
        });
    };

    const handleCountryChange = (_, newValue) => {
        userEdited.current = true;
        setFields((f) => {
            const next = { ...f, country: newValue?.code || '' };
            setFieldErrors((errs) => ({
                ...errs,
                country: getBillingFieldError('country', next) || undefined,
            }));
            return next;
        });
    };

    const handleBlur = (key) => () => {
        setFields((f) => {
            setFieldErrors((errs) => ({
                ...errs,
                [key]: getBillingFieldError(key, f) || undefined,
            }));
            return f;
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (saving) return;
        const v = validateBillingDetailsFields(fields);
        setFieldErrors(v.errors || getBillingFieldErrors(fields));
        if (!v.ok) {
            toast(TOAST_TYPES.WARN, 'Billing details', v.message);
            return;
        }
        setSaving(true);
        try {
            await onSave(v.data);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{
            border: '1px solid var(--brand-border, #E5E7EB)',
            borderRadius: '12px',
            backgroundColor: 'var(--brand-surface, #fff)',
            p: { xs: 2.5, sm: 4 },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--brand-text, #111827)' }}>
                    Billing details
                </Typography>
                <Tooltip title="Update your billing information">
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: 'var(--brand-text-muted, #9CA3AF)' }} />
                </Tooltip>
            </Box>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                columnGap: 3,
            }}>
                <Box>
                    <BillingDetailField label="Your name" placeholder="Full name on invoice" value={fields.name} onChange={handleChange('name')} onBlur={handleBlur('name')} required error={fieldErrors.name} helperText={fieldErrors.name} />
                    <BillingDetailCountryField
                        value={fields.country}
                        onChange={handleCountryChange}
                        onBlur={() => handleBlur('country')()}
                        error={fieldErrors.country}
                        helperText={fieldErrors.country}
                    />
                    <BillingDetailField label="Company name" placeholder="Organization (optional)" value={fields.company} onChange={handleChange('company')} onBlur={handleBlur('company')} error={fieldErrors.company} helperText={fieldErrors.company} />
                    <BillingDetailField label="Street" placeholder="Street address line 1" value={fields.street} onChange={handleChange('street')} onBlur={handleBlur('street')} required error={fieldErrors.street} helperText={fieldErrors.street} />
                    <BillingDetailField label="State/County" placeholder="State or region (optional)" value={fields.state} onChange={handleChange('state')} onBlur={handleBlur('state')} error={fieldErrors.state} helperText={fieldErrors.state} />
                </Box>
                <Box>
                    <BillingDetailField label="Your email" placeholder="billing@company.com" value={fields.email} onChange={handleChange('email')} onBlur={handleBlur('email')} required type="email" error={fieldErrors.email} helperText={fieldErrors.email} />
                    <BillingDetailField label="Address" placeholder="Address line 2 (optional)" value={fields.address} onChange={handleChange('address')} onBlur={handleBlur('address')} error={fieldErrors.address} helperText={fieldErrors.address} />
                    <BillingDetailField label="VAT number" placeholder="e.g. DE123456789 (optional)" value={fields.vat} onChange={handleChange('vat')} onBlur={handleBlur('vat')} error={fieldErrors.vat} helperText={fieldErrors.vat} />
                    <BillingDetailField label="Town/City" placeholder="e.g. Berlin" value={fields.city} onChange={handleChange('city')} onBlur={handleBlur('city')} error={fieldErrors.city} helperText={fieldErrors.city} />
                    <BillingDetailField label="Zip/Postal code" placeholder="e.g. 10115" value={fields.zip} onChange={handleChange('zip')} onBlur={handleBlur('zip')} required error={fieldErrors.zip} helperText={fieldErrors.zip} />
                </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving}
                    sx={{
                        backgroundColor: '#1F2A37',
                        color: '#fff',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        px: 3,
                        py: 1.1,
                        borderRadius: '8px',
                        '&:hover': { backgroundColor: '#111827' },
                    }}
                >
                    {saving ? 'Saving...' : 'Save changes'}
                </Button>
            </Box>
        </Box>
    );
};

/**
 * @param {boolean} embedded  rendered inside another page (the Settings
 *   > Billing and Payments tab) rather than as its own route. Drops the
 *   full-height floor, which would otherwise add a blank screen of padding
 *   below the section it sits in.
 */
const DQProInvoiceList = ({ embedded = false }) => {
    useGeo(); // re-render with the API's lists once they load
    const classes = useStyles();
    const upgradeClasses = useUpgradeStyles({});
    const [loading, setLoading] = useState(true);
    const [invoiceData, setInvoiceData] = useState([]);
    const [filteredInvoices, setFilteredInvoices] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
    const [selectedPaymentInvoice, setSelectedPaymentInvoice] = useState(null);
    const [publishableKey, setPublishableKey] = useState(null);
    const [programNameMap, setProgramNameMap] = useState({});
    const [dqInsiderInfo, setDqInsiderInfo] = useState(null);

    // New states for payment flow
    const [paymentMethodDialog, setPaymentMethodDialog] = useState(false);
    const [bankDialogOpen, setBankDialogOpen] = useState(false);
    const [paymentScreenshot, setPaymentScreenshot] = useState(null);
    const [clientSecret, setClientSecret] = useState(null);
    const [paymentPID, setPaymentPID] = useState('');
    const [processingPayment, setProcessingPayment] = useState(false);

    // --- Subscription Management States ---
    const [subscriptionStatus, setSubscriptionStatus] = useState(null); // 'active', 'trialing', 'past_due', 'cancel_at_period_end', 'cancelled'
    const [planKey, setPlanKey] = useState(null);
    const [planLabel, setPlanLabel] = useState('');
    const [stripeStatus, setStripeStatus] = useState('');
    const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
    const [trialDaysLeft, setTrialDaysLeft] = useState(14);
    const [periodEndDate, setPeriodEndDate] = useState('-');
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [cancelMode, setCancelMode] = useState('period_end'); // 'period_end', 'immediate'
    const [showPlans, setShowPlans] = useState(false);
    const [creditData, setCreditData] = useState(null);
    const [topUpDialogOpen, setTopUpDialogOpen] = useState(false);
    const [topUpAmount, setTopUpAmount] = useState(50);
    const [topUpClientSecret, setTopUpClientSecret] = useState(null);

    // --- Tab State --- 0=Billing details, 1=Billing history
    const [billingTab, setBillingTab] = useState(0);
    const [billingDetails, setBillingDetails] = useState({});
    const [billingFormResetKey, setBillingFormResetKey] = useState(0);

    // --- Saved Cards States ---
    const [savedCards, setSavedCards] = useState([]);
    const [cardsLoading, setCardsLoading] = useState(false);
    const [addCardDialogOpen, setAddCardDialogOpen] = useState(false);
    const [cardSetupSecret, setCardSetupSecret] = useState(null);
    const [pendingPaymentInvoice, setPendingPaymentInvoice] = useState(null);
    const [removingCardId, setRemovingCardId] = useState(null);
    const [settingPrimaryCardId, setSettingPrimaryCardId] = useState(null);

    // educator subscription plan key
    const isEducator = planKey === 'educator';

    // Check environment variable for enabling cancel flow
    const SHOW_CANCEL_SUBSCRIPTION = process.env.REACT_APP_SHOW_CANCEL_SUBSCRIPTION !== 'false';
    
    const handleOpenCancelModal = () => {
        setCancelMode('period_end');
        setCancelModalOpen(true);
    };

    const fetchSubscriptionData = useCallback(async () => {
        try {
            const response = await getSubscriptionStatusService();
            if (response && response.data) {
                const data = response.data;
                console.log("Subscription Data:", data);
                console.log("Plan key trace:", data.plan_key);

                setPlanKey(data.plan_key || DQ_PRO_PLAN_KEY.DEFAULT);
                setPlanLabel(data.plan_label);
                setStripeStatus(data.stripe_status || '');
                setCancelAtPeriodEnd(data.cancel_at_period_end || false);
                
                if (data.cancel_at_period_end && data.subscription_status === 'active') {
                    setSubscriptionStatus('cancel_at_period_end');
                } else {
                    setSubscriptionStatus(data.subscription_status );
                }
                
                if (data.subscription_renews_at) {
                    const dt = new Date(data.subscription_renews_at);
                    setPeriodEndDate(dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }));
                }
            }
        } catch (error) {
            console.error("Error fetching subscription status:", error);
        }
    }, []);

    const fetchCreditBalance = useCallback(async () => {
        try {
            const response = await getCreditBalanceService();
            if (response && response.data) {
                console.log("Credit Balance Data:", response.data);
                setCreditData(response.data);
            }
        } catch (error) {
            console.error("Error fetching credit balance:", error);
        }
    }, []);

    const fetchSavedCards = useCallback(async () => {
        setCardsLoading(true);
        try {
            const response = await listSavedCardsService();
            if (response && response.data) {
                console.log("Saved Cards Data:", response.data);
                setSavedCards(response.data.payment_methods || []);
            }
        } catch (error) {
            console.error("Error fetching saved cards:", error);
        } finally {
            setCardsLoading(false);
        }
    }, []);

    const handleOpenAddCard = async () => {
        try {
            const response = await setupCardService();
            if (response && response.data) {
                setCardSetupSecret(response.data.client_secret);
                setAddCardDialogOpen(true);
            }
        } catch (error) {
            console.error("Error creating setup intent:", error);
        }
    };

    const handleSetPrimary = async (paymentMethodId) => {
        setSettingPrimaryCardId(paymentMethodId);
        try {
            await setPrimaryCardService(paymentMethodId);
            await fetchSavedCards();
        } catch (error) {
            console.error("Error setting primary card:", error);
        } finally {
            setSettingPrimaryCardId(null);
        }
    };

    const handleRemoveCard = async (paymentMethodId) => {
        setRemovingCardId(paymentMethodId);
        try {
            await removeCardService(paymentMethodId);
            await fetchSavedCards();
        } catch (error) {
            console.error("Error removing card:", error);
        } finally {
            setRemovingCardId(null);
        }
    };

    const handleConfirmCancel = async () => {
        setProcessingPayment(true);
        try {
            const isImmediate = cancelMode === 'immediate';
            await cancelSubscriptionService(isImmediate);
            await fetchSubscriptionData(); // refresh status
        } catch (error) {
            console.error("Error cancelling subscription:", error);
        } finally {
            setProcessingPayment(false);
            setCancelModalOpen(false);
        }
    };

    const handleResumeSubscription = async () => {
        try {
            setProcessingPayment(true);
            // IMPORTANT: Use resumeSubscriptionService, NOT subscribeService.
            // subscribeService creates a brand-new Stripe subscription, which would cause
            // a double charge. resumeSubscriptionService un-cancels the existing active
            // subscription by setting cancel_at_period_end=false — no new subscription created.
            await resumeSubscriptionService();
            await fetchSubscriptionData();
        } catch (e) {
            console.error(e);
        } finally {
            setProcessingPayment(false);
        }
    };

    const handleResubscribe = () => {
        setShowPlans(true);
    };

    // Promo code state for the active payment dialog
    const [promoState, setPromoState] = useState({
        promoCode: null,
        discountAmount: 0,
        finalAmount: 0,
        promoSnapshot: null,
    });

    // Initialize Stripe promise once with useMemo to avoid recreating it
    const stripePromise = React.useMemo(() => {
        return publishableKey ? loadStripeJs(publishableKey) : null;
    }, [publishableKey]);

    const fetchBillingInfo = useCallback(async () => {
        setLoading(true);
        try {
            const response = await PaymantInfoService();
            const data = response?.data;
            if (!data) {
                console.error("No billing data found");
                setLoading(false);
                return;
            }

            console.log("Raw billing data:", data);

            // Check if we have the NEW format (invoices) or OLD format (all_billing_records)
            let rawInvoices = [];

            if (data.invoices && Array.isArray(data.invoices)) {
                // NEW FORMAT: Use consolidated invoices
                console.log("✅ Using NEW invoice format");
                rawInvoices = data.invoices;
            } else if (data.all_billing_records && Array.isArray(data.all_billing_records)) {
                // OLD FORMAT: Convert to new format for backward compatibility
                console.log("⚠️ Using OLD billing_records format - converting to new format");
                const insiders = data.insiders || [];
                rawInvoices = data.all_billing_records.map(record => {
                    const org = insiders.find(i => i.org_code === record.org_code);
                    return {
                        // Convert old format to new format
                        invoice_number: record.invoice_number,
                        invoice_id: record.invoice_number,
                        last_stats_update: record.last_stats_update,
                        month_name: record.month_name,
                        year: record.year,
                        billing_month: record.billing_month,
                        amount: record.amount,
                        status_text: record.status_text,
                        org_codes: [record.org_code],
                        programs_created: (record.programs_created || []).map(p => ({
                            ...p,
                            org_code: record.org_code,
                            org_name: org?.org_name || "Unknown"
                        })),
                        program_creation_cost_total: record.program_creation_cost_total,
                        assessments_created: (record.assessments_created || []).map(a => ({
                            ...a,
                            org_code: record.org_code,
                            org_name: org?.org_name || "Unknown"
                        })),
                        assessment_creation_cost_total: record.assessment_creation_cost_total,
                        org_breakdown: [{
                            org_code: record.org_code,
                            org_name: org?.org_name || "Unknown Organization",
                            amount: record.amount
                        }],
                        program_breakdown: (record.program_breakdown || []).map(p => ({
                            ...p,
                            org_code: p.org_code || record.org_code  // Ensure org_code is set
                        }))
                    };
                });
            } else {
                console.error("❌ Invalid data format - no invoices or all_billing_records found");
                console.log("Available keys:", Object.keys(data));
                setLoading(false);
                return;
            }

            console.log(`Found ${rawInvoices.length} invoices to process`);

            const formattedInvoices = rawInvoices.map(invoice => {
                // Now each invoice can have MULTIPLE organizations
                return {
                    invoice_number: invoice.invoice_number,
                    invoice_id: invoice.invoice_id,
                    date: invoice.last_stats_update,
                    month: `${invoice.month_name} ${invoice.year}`,
                    billing_month: invoice.billing_month,

                    // Total amount across ALL organizations
                    total_amount: invoice.amount || 0,
                    license_cost: invoice.license_cost || 0,
                    program_creation_cost_total: invoice.program_creation_cost_total || 0,
                    assessment_creation_cost_total: invoice.assessment_creation_cost_total || 0,

                    currency: 'USD',
                    status: invoice.status_text,
                    payment_date: invoice.last_stats_update,

                    // DQ Token purchases and grants (invoice_type 'dqt')
                    invoice_type: invoice.invoice_type || null,
                    dqt: invoice.dqt || null,
                    // A direct workspace's purchases (invoice_type 'direct'): licences and credits
                    units: invoice.units || null,
                    hosted_invoice_url: invoice.hosted_invoice_url || null,
                    invoice_pdf_url: invoice.invoice_pdf_url || null,

                    // Multiple organizations
                    org_codes: invoice.org_codes || [],

                    // Programs created across all orgs
                    programs_created: (invoice.programs_created || []).map(p => ({
                        org_code: p.org_code,
                        org_name: p.org_name,
                        program_id: p.program_id,
                        program_name: p.program_name,
                        program_code: p.program_code,
                        cost: p.cost || 0,
                        created_at: p.created_at,
                        no_of_sessions: p.no_of_sessions || 0,
                    })),

                    // Assessments created across all orgs
                    assessments_created: (invoice.assessments_created || []).map(a => ({
                        assessment_id: a.assessment_id,
                        assessment_code: a.assessment_code,
                        assessment_name: a.assessment_name,
                        cost: a.cost || 0,
                        created_at: a.created_at,
                    })),

                    // Map org_breakdown to organizations array
                    organizations: (invoice.org_breakdown || []).map(org => {
                        // Backend already provides programs with correct data, use it directly
                        console.log(`Org ${org.org_code} has ${org.programs?.length || 0} programs from backend`);

                        return {
                            name: org.org_name,
                            org_code: org.org_code,
                            amount: org.amount,
                            dqp: org.dqp || 0,        // Use backend's aggregated DQP
                            users: org.users || 0,    // Use backend's aggregated users
                            status: org.status || 'Pending', // Use backend's status
                            programs: org.programs || []  // Use backend's programs array directly
                        };
                    })
                };
            });

            console.log(`✅ Formatted ${formattedInvoices.length} invoices`);

            // Debug: Log first invoice structure for troubleshooting
            if (formattedInvoices.length > 0) {
                const firstInvoice = formattedInvoices[0];
                console.log('📋 First Invoice Sample:', {
                    invoice_number: firstInvoice.invoice_number,
                    org_count: firstInvoice.organizations?.length || 0,
                    organizations: firstInvoice.organizations?.map(org => ({
                        org_code: org.org_code,
                        org_name: org.name,
                        program_count: org.programs?.length || 0,
                        programs: org.programs?.map(p => p.code) || []
                    }))
                });
            }

            const sortedInvoices = formattedInvoices.sort(
                (a, b) => new Date(b.date) - new Date(a.date)
            );

            setInvoiceData(sortedInvoices);
            setFilteredInvoices(sortedInvoices);
        } catch (error) {
            console.error("❌ Error fetching billing info:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBillingInfo();
        fetchSubscriptionData();
        fetchCreditBalance();
        fetchSavedCards();
    }, [fetchBillingInfo, fetchSubscriptionData, fetchCreditBalance, fetchSavedCards]);

    useEffect(() => {
        // Fetch program names only for programs in the invoice data
        const fetchProgramNames = async () => {
            if (invoiceData.length === 0) return;
            // Collect unique program codes from all invoices
            const programCodes = new Set();
            invoiceData.forEach(invoice => {
                invoice.organizations.forEach(org => {
                    org.programs.forEach(program => {
                        programCodes.add(program.code);
                    });
                });
            });

            console.log('Unique program codes:', Array.from(programCodes));

            const moduleNamesMap = {};
            try {
                const response = await getInsiderProgramsAndAssessmentsService(Array.from(programCodes));
                console.log('Programs response:', response);
                // Map learning_programs array to a dictionary for quick lookup
                if (response?.data?.learning_programs && Array.isArray(response.data.learning_programs)) {
                    response.data.learning_programs.forEach(program => {
                        if (program.module_code && program.full_name) {
                            moduleNamesMap[program.module_code] = program.full_name;
                        }
                    });
                }
            } catch (error) {
                console.error(`Error fetching programs :`, error);
            }

            console.log('nameMap: ', moduleNamesMap);
            setProgramNameMap(moduleNamesMap);
        };

        fetchProgramNames();
    }, [invoiceData]);

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredInvoices(invoiceData);
        } else {
            const filtered = invoiceData.filter(invoice =>
                invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredInvoices(filtered.sort((a, b) => new Date(b.date) - new Date(a.date)));
        }
    }, [searchTerm, invoiceData]);

    useEffect(() => {
        (async () => {
            try {
                const config = await getStripeConfigService();
                const configKey = config?.data?.publishableKey || config?.publishableKey || null;
                setPublishableKey(configKey);
            } catch (error) {
                console.log(error);
            }
        })();
    }, []);

    useEffect(() => {
        // Fetch DQ Insider info
        (async () => {
            try {
                const response = await getDQInsiderInfoService();
                if (response?.data) {
                    console.log('DQ Insider Info:', response.data);
                    setDqInsiderInfo(response.data);
                    setBillingDetails(response.data.profile?.billing_details || {});
                }
            } catch (error) {
                console.error('Error fetching DQ Insider info:', error);
            }
        })();
    }, []);

    const handleViewInvoice = (invoice) => {
        setSelectedInvoice(invoice);
        setPreviewDialogOpen(true);
    };

    const handleDownloadInvoice = async (invoice) => {
        setLoading(true);
        try {
            const blob = await pdf(<InvoicePdfDoc invoiceData={invoice} programNameMap={programNameMap} dqInsiderInfo={dqInsiderInfo} isEducator={isEducator} />).toBlob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = (invoice.invoice_type === 'dqt' || invoice.invoice_type === 'direct')
                ? `Invoice_${invoice.invoice_number}.pdf`
                : `Invoice_${invoice.invoice_number}_${invoice.month}.pdf`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading PDF:', error);
        }
        setLoading(false);
    };

    const handleClosePreview = () => {
        setPreviewDialogOpen(false);
        setSelectedInvoice(null);
    };

    const handlePayNowClick = async (invoice) => {
        const primaryCard = savedCards.find(c => c.is_default);

        if (primaryCard) {
            // Primary card on file — charge directly
            setLoading(true);
            try {
                await payPendingInvoiceService(invoice.invoice_id, primaryCard.payment_method_id);
                await fetchBillingInfo();
            } catch (error) {
                console.error("Error paying invoice with saved card:", error);
            } finally {
                setLoading(false);
            }
        } else {
            // No saved card — open the Add Card dialog and pay after card is set up
            setPendingPaymentInvoice(invoice);
            handleOpenAddCard();
        }
    };

    const handleSelectPaymentMethod = (method) => {
        setPaymentMethodDialog(false);
        if (method === 'bank') {
            setBankDialogOpen(true);
        } else if (method === "stripe") {
            // Client secret already created in handlePayNowClick
            setOpenPaymentDialog(true);
        }
    };

    const handleBankPaymentSubmit = () => {
        console.log("Bank Payment:", {
            invoiceNumber: selectedPaymentInvoice?.invoice_number,
            pid: paymentPID,
            screenshot: paymentScreenshot,
        });
        setBankDialogOpen(false);
        setPaymentScreenshot(null);
        setPaymentPID('');
    };

    const handleClosePaymentModal = () => {
        setSelectedPaymentInvoice(null);
        setOpenPaymentDialog(false);
        setClientSecret(null); // Reset client secret to force new payment intent creation
        setPromoState({ promoCode: null, discountAmount: 0, finalAmount: 0, promoSnapshot: null });
    };

    const handleSaveBillingDetails = async (fields) => {
        try {
            const response = await updateProfileDetailsService(fields);
            if (response?.data?.billing_details) {
                const saved = response.data.billing_details;
                setBillingDetails(saved);
                setDqInsiderInfo((prev) => (
                    prev ? { ...prev, profile: { ...prev.profile, billing_details: saved } } : prev
                ));
                setBillingFormResetKey((k) => k + 1);
                toast(TOAST_TYPES.SUCCESS, 'Billing details', 'Saved successfully.');
            }
        } catch {
            // invokeApi already showed the error toast
        }
    };

    const handleOpenTopUp = () => {
        setTopUpAmount(50);
        setTopUpClientSecret(null);
        setTopUpDialogOpen(true);
    };

    const handleCloseTopUp = () => {
        setTopUpDialogOpen(false);
        setTopUpClientSecret(null);
    };

    const getStatusChipColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'paid': return { backgroundColor: '#0097b2', color: '#ffffff' };
            case 'pending': return { backgroundColor: '#ff9800', color: '#ffffff' };
            case 'cancelled': return { backgroundColor: '#9e9e9e', color: '#ffffff' };
            default: return { backgroundColor: '#ff6468', color: '#ffffff' };
        }
    };

    const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
    });

    const formatAmount = (amount) => {
        const num = Number(amount) || 0;
        return num.toFixed(2);
    };

    const isCurrentMonthInvoice = (billingMonth) => {
        if (!billingMonth) return false;
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;

        // Handle YYYYMM format (e.g., 202602 for February 2026)
        const billingMonthStr = String(billingMonth);
        const invoiceYear = parseInt(billingMonthStr.substring(0, 4), 10);
        const invoiceMonth = parseInt(billingMonthStr.substring(4, 6), 10);

        return invoiceYear === currentYear && invoiceMonth === currentMonth;
    };






    const getCardBrandIcon = (brand) => {
        const brandMap = { visa: '💳', mastercard: '💳', amex: '💳', discover: '💳' };
        return brandMap[brand?.toLowerCase()] || '💳';
    };


    return (
        <Box sx={{minHeight: embedded ? 'unset' : '88vh'}}>
            {/* ── Tabs ── */}
            <Box sx={{borderBottom: '1px solid var(--brand-border, #E5E7EB)', mb: 3}}>
                <Tabs
                    value={billingTab}
                    onChange={(_, v) => setBillingTab(v)}
                    sx={{
                        '& .MuiTabs-indicator': {backgroundColor: 'var(--brand-text, #111827)', height: '2px'},
                        '& .MuiTab-root': {textTransform: 'none', fontWeight: 500, fontSize: '0.95rem', color: 'var(--brand-text-muted, #9CA3AF)', pb: 1.5, minWidth: 130},
                        '& .Mui-selected': {color: 'var(--brand-text, #111827) !important', fontWeight: 700},
                    }}
                >
                    <Tab label="Billing details"/>
                    <Tab label="Billing history"/>
                </Tabs>
            </Box>

            {/* ── Tab 0: Billing details ── */}
            {billingTab === 0 && (
                <BillingDetailsForm
                    initialData={billingDetails}
                    resetKey={billingFormResetKey}
                    onSave={handleSaveBillingDetails}
                />
            )}

            {/* ── Tab 1: Billing history ── */}
            {billingTab === 1 && (
                <InvoiceHistoryTab
                    filteredInvoices={filteredInvoices}
                    getStatusChipColor={getStatusChipColor}
                    formatAmount={formatAmount}
                    isCurrentMonthInvoice={isCurrentMonthInvoice}
                    handleViewInvoice={handleViewInvoice}
                    handleDownloadInvoice={handleDownloadInvoice}
                    handlePayNowClick={handlePayNowClick}
                />
            )}

            {/* Add Card / Pay Now Dialog */}
            <Dialog
                open={addCardDialogOpen}
                onClose={() => { setAddCardDialogOpen(false); setCardSetupSecret(null); setPendingPaymentInvoice(null); }}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.18)' } }}
            >
                {/* Header */}
                <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid var(--brand-border, #f1f5f9)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: 'var(--brand-text, #0f172a)', letterSpacing: '-0.3px' }}>
                                {pendingPaymentInvoice ? 'Complete Payment' : 'Add New Card'}
                            </Typography>
                            <Typography sx={{ color: 'var(--brand-text-muted, #94a3b8)', fontSize: '0.78rem', mt: 0.25 }}>
                                {pendingPaymentInvoice
                                    ? `Invoice ${pendingPaymentInvoice.invoice_number} · ${pendingPaymentInvoice.month}`
                                    : 'Your card details are encrypted and secure'}
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            onClick={() => { setAddCardDialogOpen(false); setCardSetupSecret(null); setPendingPaymentInvoice(null); }}
                            sx={{ color: 'var(--brand-text-muted, #94a3b8)', mt: -0.5, mr: -1, '&:hover': { color: 'var(--brand-text, #0f172a)', backgroundColor: 'var(--brand-bg, #f1f5f9)' } }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Amount box — shown only when paying */}
                    {pendingPaymentInvoice && (
                        <Box sx={{ mt: 2, p: 1.5, mx:'auto',width:'60%', backgroundColor: 'rgba(255,100,104,0.06)', border: '1px solid rgba(255,100,104,0.2)', borderRadius: '12px', textAlign: 'center' }}>
                            <Typography sx={{ color: '#ff6468', fontWeight: 900, fontSize: '1.6rem', letterSpacing: '-0.5px', lineHeight: 1 }}>
                                ${formatAmount(pendingPaymentInvoice.total_amount)}
                            </Typography>
                            <Typography sx={{ color: 'var(--brand-text-muted, #94a3b8)', fontSize: '0.72rem', mt: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                {pendingPaymentInvoice.currency || 'USD'} · Due now
                            </Typography>
                        </Box>
                    )}
                </Box>

                <DialogContent sx={{ pt: 2.5, pb: 2, px: 3 }}>
                    {stripePromise && cardSetupSecret ? (
                        <Elements stripe={stripePromise}>
                            <AddCardForm
                                cardSetupSecret={cardSetupSecret}
                                pendingPaymentInvoice={pendingPaymentInvoice}
                                onSuccess={async (paymentMethodId) => {
                                    if (pendingPaymentInvoice) {
                                        setLoading(true);
                                        try {
                                            await payPendingInvoiceService(pendingPaymentInvoice.invoice_id, paymentMethodId);
                                            await fetchBillingInfo();
                                            setPendingPaymentInvoice(null);
                                        } catch (error) {
                                            console.error("Error paying invoice after adding card:", error);
                                        } finally {
                                            setLoading(false);
                                        }
                                    }
                                    await fetchSavedCards();
                                }}
                                onClose={() => { setAddCardDialogOpen(false); setCardSetupSecret(null); setPendingPaymentInvoice(null); }}
                            />
                        </Elements>
                    ) : (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                            <Typography sx={{ color: 'var(--brand-text-muted, #94a3b8)', fontSize: '0.875rem' }}>Preparing secure form...</Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Payment Method Dialog */}
            {/*<Dialog open={paymentMethodDialog} onClose={() => !processingPayment && setPaymentMethodDialog(false)}>*/}
            {/*    <DialogTitle>Select Payment Method</DialogTitle>*/}
            {/*    <DialogContent dividers>*/}
            {/*        <Button*/}
            {/*            variant="contained"*/}
            {/*            fullWidth*/}
            {/*            sx={{ mb: 2, backgroundColor: '#4caf50' }}*/}
            {/*            onClick={() => handleSelectPaymentMethod('stripe')}*/}
            {/*            disabled={processingPayment}*/}
            {/*        >*/}
            {/*            Pay via Stripe (Card)*/}
            {/*        </Button>*/}
            {/*        <Button*/}
            {/*            variant="outlined"*/}
            {/*            fullWidth*/}
            {/*            sx={{ borderColor: '#ff6468', color: '#ff6468' }}*/}
            {/*            onClick={() => handleSelectPaymentMethod('bank')}*/}
            {/*            disabled={processingPayment}*/}
            {/*        >*/}
            {/*            Pay via Bank Transfer*/}
            {/*        </Button>*/}
            {/*    </DialogContent>*/}
            {/*</Dialog>*/}

            {/* Bank Payment Dialog */}
            {/*<Dialog open={bankDialogOpen} onClose={() => setBankDialogOpen(false)} maxWidth="sm" fullWidth>*/}
            {/*    <DialogTitle>Bank Transfer - {selectedPaymentInvoice?.invoice_number}</DialogTitle>*/}
            {/*    <DialogContent dividers sx={{ pt: 3, pb: 2 }}>*/}
            {/*        <Typography sx={{ mb: 2 }}>*/}
            {/*            Upload a screenshot of your bank transfer and enter the Payment ID (PID) provided by your bank.*/}
            {/*        </Typography>*/}

            {/*        <Button*/}
            {/*            variant="contained"*/}
            {/*            component="label"*/}
            {/*            fullWidth*/}
            {/*            sx={{ mb: 2, backgroundColor: '#000' }}*/}
            {/*        >*/}
            {/*            Upload Screenshot*/}
            {/*            <input*/}
            {/*                type="file"*/}
            {/*                hidden*/}
            {/*                accept="image/*"*/}
            {/*                onChange={(e) => setPaymentScreenshot(e.target.files[0])}*/}
            {/*            />*/}
            {/*        </Button>*/}

            {/*        {paymentScreenshot && (*/}
            {/*            <Typography sx={{ fontSize: '0.875rem', color: '#009688' }}>*/}
            {/*                ✅ Screenshot uploaded: {paymentScreenshot.name}*/}
            {/*            </Typography>*/}
            {/*        )}*/}

            {/*        <TextField*/}
            {/*            fullWidth*/}
            {/*            label="Enter PID"*/}
            {/*            variant="outlined"*/}
            {/*            value={paymentPID}*/}
            {/*            onChange={(e) => setPaymentPID(e.target.value)}*/}
            {/*            sx={{ mt: 2 }}*/}
            {/*        />*/}
            {/*    </DialogContent>*/}

            {/*    <DialogActions>*/}
            {/*        <Button onClick={() => setBankDialogOpen(false)}>Cancel</Button>*/}
            {/*        <Button*/}
            {/*            variant="contained"*/}
            {/*            disabled={!paymentScreenshot || !paymentPID}*/}
            {/*            onClick={handleBankPaymentSubmit}*/}
            {/*        >*/}
            {/*            Submit*/}
            {/*        </Button>*/}
            {/*    </DialogActions>*/}
            {/*</Dialog>*/}

            {/* Stripe Payment Dialog — only mount Elements when dialog is actually open */}
            {stripePromise && openPaymentDialog && (
                <Elements stripe={stripePromise} options={clientSecret ? { clientSecret } : undefined}>
                    <Dialog
                        open={openPaymentDialog}
                        onClose={handleClosePaymentModal}
                        maxWidth="sm"
                        fullWidth
                        PaperProps={{ sx: { borderRadius: '12px' } }}
                    >
                        <DialogTitle sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '2px solid #ff6468',
                            pb: 2,
                            backgroundColor: 'var(--brand-bg, #fafafa)'
                        }}>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'var(--brand-text, #000)' }}>
                                    Payment
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Invoice {selectedPaymentInvoice?.invoice_number} • {selectedPaymentInvoice?.month}
                                </Typography>
                            </Box>
                            <IconButton onClick={handleClosePaymentModal} size="small">
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent sx={{ pt: 3, pb: 3 }}>
                            {selectedPaymentInvoice ? (
                                <StripePaymentForm
                                    invoice={selectedPaymentInvoice}
                                    promoState={promoState}
                                    setPromoState={setPromoState}
                                    onClose={handleClosePaymentModal}
                                    setLoading={setLoading}
                                    fetchBillingInfo={fetchBillingInfo}
                                    dqInsiderInfo={dqInsiderInfo}
                                    formatAmount={formatAmount}
                                />
                            ) : (
                                <Box sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <LoaderWithBackDrop loading={true} />
                                </Box>
                            )}
                        </DialogContent>
                    </Dialog>
                </Elements>
            )}

            {/* Top Up Credits Dialog — only mount Elements when dialog is open */}
            {stripePromise && topUpDialogOpen && (
                <Elements stripe={stripePromise}>
                    <TopUpCreditsDialog
                        open={topUpDialogOpen}
                        onClose={handleCloseTopUp}
                        topUpAmount={topUpAmount}
                        setTopUpAmount={setTopUpAmount}
                        topUpClientSecret={topUpClientSecret}
                        setTopUpClientSecret={setTopUpClientSecret}
                        dqInsiderInfo={dqInsiderInfo}
                        creditData={creditData}
                        getCreditBalanceService={getCreditBalanceService}
                        setCreditData={setCreditData}
                        setLoading={setLoading}
                    />
                </Elements>
            )}

            {/* Invoice Details Dialog */}
            <InvoiceDetailsDialog
                open={previewDialogOpen}
                onClose={handleClosePreview}
                selectedInvoice={selectedInvoice}
                dqInsiderInfo={dqInsiderInfo}
                programNameMap={programNameMap}
                formatDate={formatDate}
                formatAmount={formatAmount}
                getStatusChipColor={getStatusChipColor}
                handleDownloadInvoice={handleDownloadInvoice}
                isEducator={isEducator}
            />



            {/* Premium Cancel Subscription Modal */}
            <CancelSubscriptionDialog
                open={cancelModalOpen}
                onClose={() => setCancelModalOpen(false)}
                cancelMode={cancelMode}
                setCancelMode={setCancelMode}
                periodEndDate={periodEndDate}
                handleConfirmCancel={handleConfirmCancel}
            />



            <PlansDialog
                open={showPlans}
                onClose={() => setShowPlans(false)}
                onSelectPlan={(selectedPlanKey) => {
                    setPlanKey(selectedPlanKey);
                    fetchSubscriptionData();
                }}
                currentPlanKey={planKey}
                currentPlanTitle={planLabel}
                classes={upgradeClasses}
                forceNewSubscription={subscriptionStatus === 'cancelled'}
            />

            <LoaderWithBackDrop loading={loading || processingPayment} />
        </Box>
    );
};

export default DQProInvoiceList;
